import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { startBrowserTracingNavigationSpan } from '@sentry/angular';
import {
    getClient,
    SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN,
    SEMANTIC_ATTRIBUTE_SENTRY_SOURCE,
    Span,
    StartSpanOptions,
    stripUrlQueryAndFragment,
    timestampInSeconds,
} from '@sentry/core';
import { filter, Subject, takeUntil } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class TraceService implements OnDestroy {
    private readonly _router: Router;
    private _routingSpan: Span | undefined = undefined;
    private readonly _zone = inject(NgZone);
    private readonly _destroyed$ = new Subject<void>();

    constructor(_router: Router) {
        this._routingSpan = undefined;
        this._router = _router;
        this._navigateStart$();
    }

    ngOnDestroy(): void {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    /**
     * Start a new navigation span when the navigation starts.
     *
     * @private
     * @memberof TraceService
     */
    private _navigateStart$() {
        this._router.events
            .pipe(
                filter((event): event is NavigationStart => event instanceof NavigationStart),
                takeUntil(this._destroyed$)
            )
            .subscribe((event) => {
                const url = stripUrlQueryAndFragment(event.url);
                const client = getClient();

                if (this._routingSpan) {
                    this._routingSpan.end(timestampInSeconds());
                    this._routingSpan = undefined;
                }

                if (client) {
                    const spanOptions: StartSpanOptions = {
                        name: url,
                        attributes: {
                            [SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN]: 'auto.navigation.angular',
                            [SEMANTIC_ATTRIBUTE_SENTRY_SOURCE]: 'url',
                            url: event.url,
                            ...(event.navigationTrigger && {
                                navigationTrigger: event.navigationTrigger,
                            }),
                        },
                        startTime: Date.now(),
                    };
                    this._routingSpan = this._zone.runOutsideAngular(() =>
                        startBrowserTracingNavigationSpan(client, spanOptions)
                    );
                }
            });
    }
}
